import { PricingMode, StandardServiceFee } from "./service-fee"

export interface FeeResolutionContext {
  orderTotal: number
  changeActionCount: number
}

export function resolveStandardFeePrice(
  fee: Pick<
    StandardServiceFee,
    "price" | "pricingMode" | "percentageRate" | "minPrice" | "maxPrice"
  >,
  context: FeeResolutionContext,
): number {
  switch (fee.pricingMode) {
    case PricingMode.FIXED:
      return fee.price

    case PricingMode.PERCENTAGE: {
      const rate = fee.percentageRate ?? 0
      const min = fee.minPrice ?? 0
      const max = fee.maxPrice ?? Number.MAX_SAFE_INTEGER
      const calculated = Math.round((context.orderTotal * rate) / 100 / 5) * 5
      return Math.min(Math.max(calculated, min), max)
    }

    case PricingMode.PER_CHANGE:
      return fee.price * context.changeActionCount

    default: {
      const exhaustiveCheck: never = fee.pricingMode
      throw new Error(`Unhandled PricingMode: ${exhaustiveCheck}`)
    }
  }
}

type StandardFeePricing = Pick<
  StandardServiceFee,
  "price" | "pricingMode" | "percentageRate" | "minPrice" | "maxPrice"
>

export interface JobFeeToPrice {
  /** The price on the job, used unless the fee is priced from the order. */
  price: number
  /** The standard fee's pricing, or null for a manual fee. */
  pricing: StandardFeePricing | null
}

/**
 * Prices every fee on a job, in the order given. `context.orderTotal` is the
 * services alone. The consumables fee (PERCENTAGE) is a share of the services
 * plus every other fee on the job, so it is priced after the others.
 */
export function resolveJobFeePrices(
  fees: JobFeeToPrice[],
  context: FeeResolutionContext,
): number[] {
  const isShareOfOrder = (fee: JobFeeToPrice): boolean =>
    fee.pricing?.pricingMode === PricingMode.PERCENTAGE

  const otherPrices = fees.map((fee) => {
    if (isShareOfOrder(fee)) return 0
    if (!fee.pricing || fee.pricing.pricingMode === PricingMode.FIXED) {
      return fee.price
    }
    return resolveStandardFeePrice(fee.pricing, context)
  })
  const otherFeesTotal = otherPrices.reduce((sum, price) => sum + price, 0)

  return fees.map((fee, index) =>
    fee.pricing && isShareOfOrder(fee)
      ? resolveStandardFeePrice(fee.pricing, {
          ...context,
          orderTotal: context.orderTotal + otherFeesTotal,
        })
      : (otherPrices[index] ?? fee.price),
  )
}
